/* eslint-disable react/no-children-prop */
import { observer } from 'mobx-react';
import {
    useState,
    useEffect,
    useReducer,
    useCallback,
    useRef,
    Dispatch,
    SetStateAction,
} from 'react';
import AcvtCarousel, {
    Acvt,
    AcvtGameCard,
    AcvtGamePage,
    AcvtGamesPage,
    AcvtPlaceholderPage,
    AcvtPromoCard,
    AcvtPromotionPage,
    AcvtPromotionsPage,
    AcvtCarouselItem,
    AcvtHomePromotions,
    AcvtSmallPromoCard,
    AcvtHomePage,
    AcvtHomeGames,
    AcvtFeaturedGameCard,
    AcvtSmallGameCard,
    AcvtPromotedBanner,
} from '../../components/advanced-ct-visualisation-tool';
import {
    useAcvtStore,
    useApplicationSelectionStore,
} from '../../contexts/mobx';
import * as Colors from '../../styles/colors';
import { AcvtGame, AcvtHomeGame, AcvtPromotion } from '../../types/acvt';
import { ApplicationTargetEnum } from '../../types/app-selection';
import {
    CustomizePanel,
    PreviewBottomTabBar,
    PreviewProvider,
    PreviewThemeProvider,
    usePreview,
    usePreviewTheme,
    PreviewTabKey,
} from '../../components/preview';
import StoresIcon from '../../icons/Icon_Stores_1X.svg?react';
import CarwashIcon from '../../icons/CarwashIcon.svg?react';
import style from './style.module.scss';
import { PageTopBar } from '../../components/page-top-bar';

/**
 * Discriminated state for the home/promotions/games drill-down. Only
 * one card can be open at a time — the union encodes that constraint
 * directly, replacing the prior bag of four booleans whose combinations
 * could express invalid states (e.g. two cards open at once).
 */
type OpenCardKind = 'none' | 'home-promo' | 'home-game' | 'promo' | 'game';

type OpenCardAction =
    | { type: 'open-home-promo' }
    | { type: 'open-home-game' }
    | { type: 'open-promo' }
    | { type: 'open-game' }
    | { type: 'close' };

const openCardReducer = (
    _state: OpenCardKind,
    action: OpenCardAction
): OpenCardKind => {
    switch (action.type) {
        case 'open-home-promo':
            return 'home-promo';
        case 'open-home-game':
            return 'home-game';
        case 'open-promo':
            return 'promo';
        case 'open-game':
            return 'game';
        case 'close':
            return 'none';
    }
};

type CardBooleans = {
    homePromoCard: boolean;
    homeGameCard: boolean;
    promoCard: boolean;
    gameCard: boolean;
};

const AcvtPageInner = observer(() => {
    const { region, language: lang } = usePreview();
    const theme = usePreviewTheme();
    const acvtStore = useAcvtStore();

    const [openCard, dispatchOpenCard] = useReducer(openCardReducer, 'none');

    const openCardRef = useRef(openCard);
    openCardRef.current = openCard;

    const setIsCardOpened: Dispatch<SetStateAction<CardBooleans>> = useCallback(
        (updater) => {
            const current: CardBooleans = {
                homePromoCard: openCardRef.current === 'home-promo',
                homeGameCard: openCardRef.current === 'home-game',
                promoCard: openCardRef.current === 'promo',
                gameCard: openCardRef.current === 'game',
            };
            const next =
                typeof updater === 'function' ? updater(current) : updater;
            if (next.homePromoCard)
                dispatchOpenCard({ type: 'open-home-promo' });
            else if (next.homeGameCard)
                dispatchOpenCard({ type: 'open-home-game' });
            else if (next.promoCard) dispatchOpenCard({ type: 'open-promo' });
            else if (next.gameCard) dispatchOpenCard({ type: 'open-game' });
            else dispatchOpenCard({ type: 'close' });
        },
        []
    );

    const [selectedTab, setSelectedTab] = useState<PreviewTabKey>('home');

    // When the user switches BU, the active tab may no longer be part of
    // the new theme (e.g. carwash exists in QC but not CA). Reset to
    // home so the user lands on a tab that's always present.
    useEffect(() => {
        if (!theme.tabs.includes(selectedTab)) {
            setSelectedTab('home');
        }
    }, [theme.tabs, selectedTab]);

    const [singlePromo, setSinglePromo] = useState<AcvtPromotion>();

    const [singleHomePromo, setSingleHomePromo] = useState<AcvtPromotion>();

    const [singleGame, setSingleGame] = useState<AcvtGame>();

    const [singleHomeGame, setSingleHomeGame] = useState<AcvtHomeGame>();

    const [offers, setOffers] = useState<string>();

    const [couponCategories, setCouponCategories] = useState<string>();

    const [inStoreCategories, setInStoreCategories] = useState<string>();

    useEffect(() => {
        acvtStore.fetchAcvtPromotions(lang, region);
        acvtStore.fetchAcvtGames(lang, region);
        acvtStore.fetchAcvtHome(lang, region);
    }, [acvtStore, lang, region]);

    useEffect(() => {
        setSinglePromo((prevSinglePromo) =>
            acvtStore.acvtPromotionList.find(
                (element) => element.id === prevSinglePromo?.id
            )
        );
    }, [acvtStore.acvtPromotionList]);

    // The promo/in-store filter selections only make sense within the
    // active language: a coupon category title in EN does not match the
    // FR list. Reset them whenever the user switches the preview
    // language via the CustomizePanel.
    useEffect(() => {
        setOffers(undefined);
        setCouponCategories(undefined);
        setInStoreCategories(undefined);
    }, [lang]);

    const getPromotionalOffers = () => {
        const promotionalOffersList = acvtStore.acvtPromotionList.map(
            (promo) => promo.promotionalOffer?.title
        );
        const uniqPromotionalOffersList = [...new Set(promotionalOffersList)];
        return uniqPromotionalOffersList;
    };

    const getCouponCategoriesList = () => {
        const categoriesList = acvtStore.acvtPromotionList.map((promo) =>
            promo.type === 'coupon' ? promo.category.title : null
        );

        const uniqCategoriesList = [...new Set(categoriesList)].filter(
            (n) => n
        );
        return uniqCategoriesList;
    };

    const getInStoreCategoriesList = () => {
        const categoriesList = acvtStore.acvtPromotionList.map((promo) =>
            promo.type === 'in_store' ? promo.category.title : null
        );

        const uniqCategoriesList = [...new Set(categoriesList)].filter(
            (n) => n
        );
        return uniqCategoriesList;
    };

    const setPromo = (promo: AcvtPromotion) => {
        setSinglePromo(promo);
    };

    const setHomePromo = (homePromo: AcvtPromotion) => {
        setSingleHomePromo(homePromo);
    };

    const changeOffers = (selection: string) => {
        setOffers(selection);
    };

    const changeCouponCategories = (selection: string) => {
        setCouponCategories(selection);
    };

    const changeInStoreCategories = (selection: string) => {
        setInStoreCategories(selection);
    };

    const setGame = (game: AcvtGame) => {
        setSingleGame(game);
    };

    const setHomeGame = (game: AcvtHomeGame) => {
        setSingleHomeGame(game);
    };

    return (
        <>
            <PageTopBar title="App Preview" />
            <div className={`${style.content} content_with_margins`}>
                <div className={style.canvas_column} key={lang}>
                    {(selectedTab === 'home' &&
                        (openCard === 'home-promo' ? (
                            <Acvt
                                backgroundColor="white"
                                children={
                                    <>
                                        <AcvtPromotionPage
                                            promoObject={singleHomePromo}
                                            opened={setIsCardOpened}
                                        />
                                        <PreviewBottomTabBar
                                            selectedTab={selectedTab}
                                            onTabChange={setSelectedTab}
                                        />
                                    </>
                                }
                            />
                        ) : openCard === 'home-game' ? (
                            <Acvt
                                backgroundColor={Colors.UNKNOWN_BACKGROUND_RED}
                                children={
                                    <>
                                        <AcvtGamePage
                                            key={singleHomeGame?.id}
                                            gameObject={singleHomeGame}
                                            opened={setIsCardOpened}
                                        />
                                        <PreviewBottomTabBar
                                            selectedTab={selectedTab}
                                            onTabChange={setSelectedTab}
                                        />
                                    </>
                                }
                            />
                        ) : (
                            <Acvt
                                backgroundColor={Colors.UNKNOWN_BACKGROUND_RED}
                                children={
                                    <>
                                        <AcvtHomePage
                                            featuredCarousel={
                                                <AcvtCarousel>
                                                    {acvtStore.acvtHomeObject.featured?.map(
                                                        (featured) => (
                                                            <AcvtCarouselItem
                                                                key={
                                                                    featured.id
                                                                }
                                                                image={
                                                                    featured.backgroundImage
                                                                }
                                                                title={
                                                                    featured.textTitle ??
                                                                    featured
                                                                        .promotion
                                                                        ?.title ??
                                                                    undefined
                                                                }
                                                                subtitle={
                                                                    featured
                                                                        .promotion
                                                                        ?.subTitle ??
                                                                    undefined
                                                                }
                                                                cta={
                                                                    featured.cta ??
                                                                    undefined
                                                                }
                                                                backgroundColor={
                                                                    featured
                                                                        .promotion
                                                                        ?.backgroundColor
                                                                }
                                                                titleColor={
                                                                    featured
                                                                        .promotion
                                                                        ?.promotedBannerTextColor
                                                                }
                                                            />
                                                        )
                                                    )}
                                                </AcvtCarousel>
                                            }
                                            promos={
                                                <AcvtHomePromotions
                                                    viewAll={setSelectedTab}
                                                    promotedBanners={(() => {
                                                        const promosWithBackground = [
                                                            ...(acvtStore.acvtHomeObject.coupons?.filter(
                                                                (promo) =>
                                                                    promo.backgroundColor
                                                            ) || []),
                                                            ...(acvtStore.acvtHomeObject.in_stores?.filter(
                                                                (promo) =>
                                                                    promo.backgroundColor
                                                            ) || []),
                                                        ];

                                                        const oldestPromo = promosWithBackground.sort(
                                                            (a, b) => {
                                                                const dateA = new Date(
                                                                    a.startDate as string
                                                                ).getTime();
                                                                const dateB = new Date(
                                                                    b.startDate as string
                                                                ).getTime();
                                                                return (
                                                                    dateA -
                                                                    dateB
                                                                );
                                                            }
                                                        )[0];

                                                        return oldestPromo ? (
                                                            <AcvtPromotedBanner
                                                                key={
                                                                    oldestPromo.id
                                                                }
                                                                promoObject={
                                                                    oldestPromo
                                                                }
                                                                onClick={() => {
                                                                    setIsCardOpened(
                                                                        (
                                                                            prevState
                                                                        ) => ({
                                                                            ...prevState,
                                                                            homePromoCard: true,
                                                                        })
                                                                    );
                                                                    setHomePromo(
                                                                        oldestPromo
                                                                    );
                                                                }}
                                                            />
                                                        ) : null;
                                                    })()}
                                                    couponCards={acvtStore.acvtHomeObject.coupons?.map(
                                                        (promo) => (
                                                            <AcvtSmallPromoCard
                                                                key={promo.id}
                                                                promoObject={
                                                                    promo
                                                                }
                                                                opened={
                                                                    setIsCardOpened
                                                                }
                                                                setSinglePromo={
                                                                    setHomePromo
                                                                }
                                                            />
                                                        )
                                                    )}
                                                    inStoreCards={acvtStore.acvtHomeObject.in_stores?.map(
                                                        (promo) => (
                                                            <AcvtSmallPromoCard
                                                                key={promo.id}
                                                                promoObject={
                                                                    promo
                                                                }
                                                                opened={
                                                                    setIsCardOpened
                                                                }
                                                                setSinglePromo={
                                                                    setHomePromo
                                                                }
                                                            />
                                                        )
                                                    )}
                                                />
                                            }
                                            games={
                                                <AcvtHomeGames
                                                    smallCards={acvtStore.acvtHomeObject.games
                                                        ?.filter(
                                                            (game) =>
                                                                !game.isFeatured
                                                        )
                                                        .map((game) => (
                                                            <AcvtSmallGameCard
                                                                key={game.id}
                                                                gameObject={
                                                                    game
                                                                }
                                                                language={lang}
                                                                opened={
                                                                    setIsCardOpened
                                                                }
                                                                setSingleGame={
                                                                    setHomeGame
                                                                }
                                                            />
                                                        ))}
                                                    featuredCards={acvtStore.acvtHomeObject.games
                                                        ?.filter(
                                                            (game) =>
                                                                game.isFeatured
                                                        )
                                                        .map((game) => (
                                                            <AcvtFeaturedGameCard
                                                                key={game.id}
                                                                gameObject={
                                                                    game
                                                                }
                                                                language={lang}
                                                                opened={
                                                                    setIsCardOpened
                                                                }
                                                                setSingleGame={
                                                                    setHomeGame
                                                                }
                                                            />
                                                        ))}
                                                />
                                            }
                                        />
                                        <PreviewBottomTabBar
                                            selectedTab={selectedTab}
                                            onTabChange={setSelectedTab}
                                        />
                                    </>
                                }
                            />
                        ))) ||
                        (selectedTab === 'map' && (
                            <Acvt
                                backgroundColor="white"
                                children={
                                    <>
                                        <AcvtPlaceholderPage>
                                            <StoresIcon />
                                        </AcvtPlaceholderPage>
                                        <PreviewBottomTabBar
                                            selectedTab={selectedTab}
                                            onTabChange={setSelectedTab}
                                        />
                                    </>
                                }
                            />
                        )) ||
                        (selectedTab === 'products' && (
                            <Acvt
                                backgroundColor="white"
                                children={
                                    openCard === 'promo' ? (
                                        <>
                                            <AcvtPromotionPage
                                                promoObject={singlePromo}
                                                opened={setIsCardOpened}
                                            />
                                            <PreviewBottomTabBar
                                                selectedTab={selectedTab}
                                                onTabChange={setSelectedTab}
                                            />
                                        </>
                                    ) : (
                                        <>
                                            <AcvtPromotionsPage
                                                offersList={getPromotionalOffers()}
                                                couponCategoriesList={getCouponCategoriesList()}
                                                inStoreCategoriesList={getInStoreCategoriesList()}
                                                selectedOffer={offers}
                                                selectedCouponCategory={
                                                    couponCategories
                                                }
                                                selectedInStoreCategory={
                                                    inStoreCategories
                                                }
                                                changeOffers={changeOffers}
                                                changeCouponCategories={
                                                    changeCouponCategories
                                                }
                                                changeInStoreCategories={
                                                    changeInStoreCategories
                                                }
                                                couponCards={
                                                    <div
                                                        className={
                                                            style.promotions_cards
                                                        }
                                                    >
                                                        {acvtStore.acvtPromotionList.map(
                                                            (promo) =>
                                                                promo.type ===
                                                                    'coupon' &&
                                                                (promo
                                                                    .promotionalOffer
                                                                    ?.title ===
                                                                    offers ||
                                                                    offers ===
                                                                        undefined) &&
                                                                (promo.category
                                                                    .title ===
                                                                    couponCategories ||
                                                                    couponCategories ===
                                                                        undefined) && (
                                                                    <AcvtPromoCard
                                                                        key={
                                                                            promo.id
                                                                        }
                                                                        promoObject={
                                                                            promo
                                                                        }
                                                                        opened={
                                                                            setIsCardOpened
                                                                        }
                                                                        setSinglePromo={
                                                                            setPromo
                                                                        }
                                                                    />
                                                                )
                                                        )}
                                                    </div>
                                                }
                                                inStoreCards={
                                                    <div
                                                        className={
                                                            style.promotions_cards
                                                        }
                                                    >
                                                        {acvtStore.acvtPromotionList.map(
                                                            (promo) =>
                                                                promo.type ===
                                                                    'in_store' &&
                                                                (promo.category
                                                                    .title ===
                                                                    inStoreCategories ||
                                                                    inStoreCategories ===
                                                                        undefined) && (
                                                                    <AcvtPromoCard
                                                                        key={
                                                                            promo.id
                                                                        }
                                                                        promoObject={
                                                                            promo
                                                                        }
                                                                        opened={
                                                                            setIsCardOpened
                                                                        }
                                                                        setSinglePromo={
                                                                            setPromo
                                                                        }
                                                                    />
                                                                )
                                                        )}
                                                    </div>
                                                }
                                            />
                                            <PreviewBottomTabBar
                                                selectedTab={selectedTab}
                                                onTabChange={setSelectedTab}
                                            />
                                        </>
                                    )
                                }
                            />
                        )) ||
                        (selectedTab === 'carwash' && (
                            <Acvt
                                backgroundColor="white"
                                children={
                                    <>
                                        <AcvtPlaceholderPage>
                                            <CarwashIcon />
                                        </AcvtPlaceholderPage>
                                        <PreviewBottomTabBar
                                            selectedTab={selectedTab}
                                            onTabChange={setSelectedTab}
                                        />
                                    </>
                                }
                            />
                        )) ||
                        (selectedTab === 'age-gated' && (
                            <Acvt
                                backgroundColor="white"
                                children={
                                    <>
                                        <AcvtPlaceholderPage>
                                            <StoresIcon />
                                        </AcvtPlaceholderPage>
                                        <PreviewBottomTabBar
                                            selectedTab={selectedTab}
                                            onTabChange={setSelectedTab}
                                        />
                                    </>
                                }
                            />
                        )) ||
                        (selectedTab === 'more' &&
                            (openCard === 'game' ? (
                                <Acvt
                                    backgroundColor={
                                        Colors.UNKNOWN_BACKGROUND_RED
                                    }
                                    children={
                                        <>
                                            <AcvtGamePage
                                                key={singleGame?.id}
                                                gameObject={singleGame}
                                                opened={setIsCardOpened}
                                            />
                                            <PreviewBottomTabBar
                                                selectedTab={selectedTab}
                                                onTabChange={setSelectedTab}
                                            />
                                        </>
                                    }
                                />
                            ) : (
                                <Acvt
                                    backgroundColor={
                                        Colors.UNKNOWN_CHART_BLUE_ALT
                                    }
                                    children={
                                        <>
                                            <AcvtGamesPage
                                                cards={acvtStore.acvtGamesList.map(
                                                    (game) => (
                                                        <AcvtGameCard
                                                            key={game?.id}
                                                            gameObject={game}
                                                            opened={
                                                                setIsCardOpened
                                                            }
                                                            setSingleGame={
                                                                setGame
                                                            }
                                                        />
                                                    )
                                                )}
                                            />
                                            <PreviewBottomTabBar
                                                selectedTab={selectedTab}
                                                onTabChange={setSelectedTab}
                                            />
                                        </>
                                    }
                                />
                            )))}
                </div>
                <CustomizePanel />
            </div>
        </>
    );
});

/**
 * Bridge between `PreviewProvider` and `PreviewThemeProvider`: pulls the
 * active region out of the preview context and forwards it to the theme
 * provider. Lives as its own component so that `AcvtPageInner` can call
 * `usePreviewTheme()` from inside the theme provider's subtree.
 */
const AcvtPageThemed = observer(() => {
    const { region } = usePreview();
    return (
        <PreviewThemeProvider region={region}>
            <AcvtPageInner />
        </PreviewThemeProvider>
    );
});

export const AcvtPage = observer(() => {
    const applicationSelection = useApplicationSelectionStore();
    // `appTarget` is `undefined` when the global app selector sits on
    // "All Regions". The preview always needs a concrete BU, so default
    // to QC — the historical behaviour of this page (red Couche-Tard
    // theme).
    const initialRegion =
        applicationSelection.appTarget ?? ApplicationTargetEnum.QC;

    return (
        <PreviewProvider initialRegion={initialRegion}>
            <AcvtPageThemed />
        </PreviewProvider>
    );
});
